// ==========================================
// AntVoucherFlow — Centralized Configuration
// ==========================================

// ==========================================
// 1. STATUS COLORS + ICONS
// Thiết kế thống nhất 6 trạng thái:
//   1. Đang xử lý (processing) — Blue border, blue dot
//   2. Huỷ (cancel) — Red border, ✕ icon
//   3. Hoàn tất (completed) — Cyan border, ✓ icon
//   4. Chưa tạo (expected) — Dashed gray border
//   5. Có lỗi (error) — Red bg tint, ⚠ icon
//   6. Cần lưu ý (warning) — Orange border, ⚠ icon
// ==========================================

export const STATUS_COLORS = {
    // Mới tạo
    New: { bg: '#FFFFFF', text: '#64748B', border: '#CBD5E1', dot: '#94A3B8', icon: null },
    // Đang xử lý — nổi bật tone xanh lá
    Release: { bg: '#F0FDF4', text: '#166534', border: '#86EFAC', dot: '#22C55E', icon: null },
    Open: { bg: '#F0FDF4', text: '#166534', border: '#86EFAC', dot: '#22C55E', icon: null },
    Posted: { bg: '#F0FDF4', text: '#166534', border: '#86EFAC', dot: '#22C55E', icon: null },

    // Hoàn tất — dùng dot (point) thay vì check icon
    Close: { bg: '#FFFFFF', text: '#0369A1', border: '#7DD3FC', dot: '#0EA5E9', icon: null },

    // Huỷ / Từ chối
    Cancel: { bg: '#FFF5F5', text: '#991B1B', border: '#FCA5A5', dot: '#EF4444', icon: '✕', iconColor: '#EF4444' },
    Reject: { bg: '#FFF5F5', text: '#991B1B', border: '#FCA5A5', dot: '#EF4444', icon: '✕', iconColor: '#EF4444' },

    // Chưa tạo (expected)
    _expected: { bg: '#FAFAFA', text: '#94A3B8', border: '#E2E8F0', dot: '#CBD5E1', dashBorder: true, icon: null },

    // Current node highlight
    _current: { shadow: '0 0 0 3px rgba(59,130,246,0.35)' },

    // Disabled / No access
    _disabled: { bg: '#F8F8F8', text: '#6B7280', border: '#D1D5DB', dot: '#9CA3AF', opacity: 0.85, icon: null },

    // Lỗi
    _error: { bg: '#FEF2F2', text: '#991B1B', border: '#F87171', dot: '#EF4444', icon: '⚠', iconColor: '#EF4444' },

    // Cần lưu ý
    _warning: { bg: '#FFFBEB', text: '#92400E', border: '#FCD34D', dot: '#F59E0B', icon: '⚠', iconColor: '#F59E0B' },

    // Kitchen sub-flow
    Done: { bg: '#FFFFFF', text: '#065F46', border: '#6EE7B7', dot: '#10B981', icon: null },
    Current: { bg: '#FFFFFF', text: '#92400E', border: '#FCD34D', dot: '#F59E0B', pulse: true, icon: null },
    Waiting: { bg: '#FAFAFA', text: '#94A3B8', border: '#E2E8F0', dot: '#CBD5E1', icon: null },

    // Deposit Gate
    Ready: { bg: '#FFFFFF', text: '#065F46', border: '#6EE7B7', dot: '#10B981', icon: null },

    _default: { bg: '#FFFFFF', text: '#475569', border: '#CBD5E1', dot: '#94A3B8', icon: null }
};

export const STATUS_COLORS_BY_TYPE = {
    ARDelivery: {
        Transported: { bg: '#FFFFFF', text: '#1E40AF', border: '#93C5FD', dot: '#3B82F6', icon: null },
        Delivered: { bg: '#FFFFFF', text: '#065F46', border: '#6EE7B7', dot: '#10B981', icon: null }
    },
    LGTransport: {
        Picked: { bg: '#FFFFFF', text: '#1E40AF', border: '#93C5FD', dot: '#3B82F6', icon: null },
        Dropped: { bg: '#FFFFFF', text: '#0369A1', border: '#7DD3FC', dot: '#0EA5E9', icon: null }
    }
};

export function getStatusColor(status, serviceType, existence, isDisabled) {
    if (isDisabled) return STATUS_COLORS._disabled;
    if (existence === 'expected') return STATUS_COLORS._expected;
    if (existence === 'error') return STATUS_COLORS._error;
    if (existence === 'warning') return STATUS_COLORS._warning;
    if (!status) return STATUS_COLORS._default;

    let typeColors = STATUS_COLORS_BY_TYPE[serviceType];
    if (typeColors && typeColors[status]) return typeColors[status];

    return STATUS_COLORS[status] || STATUS_COLORS._default;
}

// ==========================================
// 2. DRILLDOWN URLs — Click node navigate
// ==========================================

export const DRILLDOWN_CONFIG = {
    // AR
    ARAppointment: { url: '/ar/ARAppointment/', openInNewTab: false, whenUserScreen: 'ARAppointments' },
    ARRentalBooking: { url: null, clickable: false, whenUserScreen: null },
    AROrder: { url: '/ar/AROrder/', openInNewTab: false, whenUserScreen: 'AROrders' },
    ARIssue: { url: '/ar/ARIssue/', openInNewTab: false, whenUserScreen: 'ARIssues' },
    ARReturn: { url: '/ar/ARReturn/', openInNewTab: false, whenUserScreen: 'ARReturns' },
    ARDelivery: { url: null, tooltip: true, whenUserScreen: null },
    ARCredit: { url: '/ar/ARCredit/', openInNewTab: false, whenUserScreen: 'ARCredits' },
    ARInvoice: { url: '/ar/ARInvoice/', openInNewTab: false, whenUserScreen: 'ARInvoices' },

    // AP
    APOrder: { url: '/ap/APOrder/', openInNewTab: false, whenUserScreen: 'APOrders' },
    APReceive: { url: '/ap/APReceive/', openInNewTab: false, whenUserScreen: 'APReceives' },
    APReturn: { url: '/ap/APReturn/', openInNewTab: false, whenUserScreen: 'APReturns' },
    APDelivery: { url: null, tooltip: true, whenUserScreen: null },
    APCredit: { url: '/ap/APCredit/', openInNewTab: false, whenUserScreen: 'APCredits' },
    APInvoice: { url: '/ap/APInvoice/', openInNewTab: false, whenUserScreen: 'APInvoices' },
    APImportCost: { url: '/ap/APImportCost/', openInNewTab: false, whenUserScreen: 'APImportCosts' },

    // CA
    CARequest: { url: '/ca/CARequest/', openInNewTab: false, whenUserScreen: null },
    CAReceive: { url: '/ca/CAReceive/', openInNewTab: false, whenUserScreen: 'CAReceives' },
    CAIssue: { url: '/ca/CAIssue/', openInNewTab: false, whenUserScreen: 'CAIssues' },
    CAApply: { url: null, tooltip: true, whenUserScreen: null },

    // IC
    ICIssue: { url: '/inv/ICIssue/', openInNewTab: false, whenUserScreen: 'ICIssues' },
    ICReceipt: { url: '/inv/ICReceipt/', openInNewTab: false, whenUserScreen: 'ICReceipts' },

    // LG
    LGOrder: { url: '/lg/LGOrder/', openInNewTab: false, whenUserScreen: 'LGOrders' },
    LGTransport: { url: '/lg/LGTransport/', openInNewTab: false, whenUserScreen: 'LGTransports' },

    // GL
    GLJournal: { url: '/gl/GLJournal/', openInNewTab: true, whenUserScreen: 'GLJournals' },
    PNLJournal: { url: null, tooltip: true, whenUserScreen: null },

    // Thẻ/Ví
    ServiceCard: { url: '/ar/ServiceCard/', openInNewTab: false, whenUserScreen: 'ServiceCards' },
    DepositCard: { url: '/ar/DepositCard/', openInNewTab: false, whenUserScreen: 'DepositCards' },

    // Đặc biệt
    KitchenStep: { url: null, clickable: false, whenUserScreen: null },
    DepositGate: { url: null, clickable: false, whenUserScreen: null },

    _default: { url: null, clickable: false, tooltip: true, whenUserScreen: null }
};

export function getDrilldown(serviceType) {
    return DRILLDOWN_CONFIG[serviceType] || DRILLDOWN_CONFIG._default;
}

// ==========================================
// 3. NODE DISPLAY
// ==========================================

export const NODE_DISPLAY = {
    DepositGate: { customRender: 'progressBar' },
    _default: {}
};

export function getNodeDisplay(serviceType) {
    return NODE_DISPLAY[serviceType] || NODE_DISPLAY._default;
}
